import { useEffect, useState, useSyncExternalStore } from "react";

export type PolicySection = { id: string; title: string; body: string };
export type PolicyDoc = {
  title: string;
  description: string;
  updated: string;
  effective: string;
  version: string;
  author: string;
  appliesTo: string;
  numbered?: boolean;
  sections: PolicySection[];
};
export type PolicyKey = "privacy" | "terms" | "cancellation";
export type PoliciesState = Record<PolicyKey, PolicyDoc>;

const STORAGE_KEY = "spayzo.policies.v2";

export const DEFAULT_POLICIES: PoliciesState = {
  privacy: {
    title: "Privacy policy",
    description: "What data we collect, how we use it, and the choices you have.",
    updated: "May 1, 2026",
    effective: "Jan 01, 2025",
    version: "v3.2",
    author: "Omar Al Rashid",
    appliesTo: "All platform users — Merchants & Guests",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        body: 'This Privacy Policy explains how Spayzo, Inc. ("Spayzo", "we", "us") collects, uses, and shares information about you when you use our website, mobile apps and services.',
      },
      {
        id: "information-we-collect",
        title: "Information we collect",
        body: "- **Account data:** name, email, password and profile photo.\n- **Booking data:** spaces you view, save and book.\n- **Payment data:** processed securely by our payment partners — we never store full card numbers.\n- **Device data:** IP address, browser, operating system and app version.",
      },
      {
        id: "how-we-use",
        title: "How we use information",
        body: "- Provide, maintain and improve the Spayzo platform.\n- Process payments and prevent fraud.\n- Send transactional emails and, with your consent, marketing updates.\n- Comply with legal obligations.",
      },
      {
        id: "sharing",
        title: "How we share information",
        body: "We share data with hosts to fulfill bookings, with service providers (payments, hosting, analytics) under contractual safeguards, and with authorities when legally required. We never sell personal information.",
      },
      {
        id: "your-rights",
        title: "Your rights",
        body: "You can access, correct, export or delete your personal data at any time from your dashboard or by contacting privacy@spayzo.app.",
      },
      {
        id: "security",
        title: "Security",
        body: "Data is encrypted in transit (TLS 1.3) and at rest. We follow industry best-practices for access control and incident response.",
      },
      {
        id: "contact",
        title: "Contact us",
        body: "Questions? Email privacy@spayzo.app.",
      },
    ],
  },
  terms: {
    title: "Terms & conditions",
    description: "The rules and agreements that apply when you use Spayzo.",
    updated: "May 1, 2026",
    effective: "Jan 01, 2025",
    version: "v2.0",
    author: "Omar Al Rashid",
    appliesTo: "All platform users",
    numbered: true,
    sections: [
      { id: "acceptance", title: "Acceptance of terms", body: "By creating an account or using Spayzo you agree to these Terms. If you do not agree, do not use the service." },
      { id: "eligibility", title: "Eligibility", body: "You must be at least 18 years old and able to form a binding contract to use Spayzo." },
      { id: "accounts", title: "Your account", body: "You're responsible for keeping your credentials confidential and for all activity under your account. Notify us immediately of any unauthorized access." },
      { id: "bookings", title: "Bookings & payments", body: "Bookings create a direct contract between you and the host. Spayzo facilitates payment and applies service fees and applicable taxes shown at checkout." },
      { id: "conduct", title: "User conduct", body: "- Do not misuse spaces or violate house rules.\n- Do not post unlawful, fraudulent or harmful content.\n- Do not attempt to circumvent platform fees." },
      { id: "content", title: "Content & intellectual property", body: "You retain ownership of content you submit but grant Spayzo a worldwide, royalty-free license to host and display it for operating the service." },
      { id: "liability", title: "Limitation of liability", body: "To the maximum extent permitted by law, Spayzo is not liable for indirect or consequential damages. Our aggregate liability is limited to fees you paid in the prior 12 months." },
      { id: "termination", title: "Termination", body: "We may suspend or terminate accounts that violate these Terms. You may close your account at any time from your dashboard." },
      { id: "changes", title: "Changes to these terms", body: "We may update these Terms periodically. Material changes will be communicated by email or in-app notice at least 14 days before they take effect." },
      { id: "governing-law", title: "Governing law", body: "These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles." },
    ],
  },
  cancellation: {
    title: "Cancellation policy",
    description: "How cancellations, refunds and no-shows are handled on Spayzo.",
    updated: "May 1, 2026",
    effective: "Jan 01, 2025",
    version: "v1.4",
    author: "Omar Al Rashid",
    appliesTo: "All bookings",
    sections: [
      { id: "overview", title: "Overview", body: "Spayzo applies a standard, transparent cancellation policy across all bookings unless the host has opted into a stricter or more flexible alternative — clearly shown on the space page." },
      { id: "guest-cancellations", title: "Guest cancellations", body: "- **More than 48 hours before start:** 100% refund, including service fees.\n- **24–48 hours before start:** 50% refund of the booking subtotal.\n- **Less than 24 hours before start:** non-refundable." },
      { id: "host-cancellations", title: "Host cancellations", body: "If a host cancels, you receive a full refund automatically and a credit toward a future booking. Repeat cancellations may result in penalties for the host." },
      { id: "no-shows", title: "No-shows", body: "If you don't arrive within 30 minutes of your booking start time and have not contacted the host, the booking is considered a no-show and is non-refundable." },
      { id: "extenuating-circumstances", title: "Extenuating circumstances", body: "Serious illness, natural disasters, or government travel restrictions may qualify for a full refund regardless of timing. Contact support with documentation." },
      { id: "refund-timeline", title: "Refund timeline", body: "Approved refunds are returned to your original payment method within 5–10 business days." },
    ],
  },
};

let memory: PoliciesState = DEFAULT_POLICIES;
const listeners = new Set<() => void>();

function load(): PoliciesState {
  if (typeof window === "undefined") return DEFAULT_POLICIES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_POLICIES;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_POLICIES, ...parsed };
  } catch {
    return DEFAULT_POLICIES;
  }
}

function save(state: PoliciesState) {
  memory = state;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function usePolicies(): [PoliciesState, (updater: (s: PoliciesState) => PoliciesState) => void] {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    memory = load();
    setHydrated(true);
    listeners.forEach((l) => l());
  }, []);
  const state = useSyncExternalStore(
    subscribe,
    () => memory,
    () => DEFAULT_POLICIES,
  );
  const update = (updater: (s: PoliciesState) => PoliciesState) => save(updater(state));
  return [hydrated ? state : memory, update];
}

export function usePolicy(key: PolicyKey): PolicyDoc {
  const [policies] = usePolicies();
  return policies[key];
}
